/**
 * The print job — one state machine, one promise.
 *
 * `idle → configuring? → loading → preview → idle`
 *
 * The promise: NOTHING is mounted into the print window until the sheet is
 * fully resolved. Every way a print run fails (a blocked popup, too many rows,
 * a failed fetch) fails after the operator asked and before anything is
 * visible, so a machine that mounted early would leave a half-built sheet one
 * Ctrl+P away from being paper.
 *
 * The DESTINATION is a separate window holding a self-contained document
 * (#1902), not a portal inside the app. That is why the window is opened
 * BEFORE the rows are fetched: `window.open` only survives the popup blocker
 * inside the user gesture's synchronous call stack, and both entry points
 * (`start` with no options, `confirm` from the dialog) are click handlers. The
 * window then shows a status line until the sheet lands — or the failure, so
 * a run that dies mid-fetch never leaves a blank window on screen.
 *
 * The hook owns state and sequencing only. It does not render the portal and
 * does not know what a sheet looks like — `renderDefault` comes from the list
 * (which owns the columns) and `config.buildSheet` from the callsite.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useResourceKit } from '@resource-kit/core/react';
import { openPrintDocument, type PrintDocument } from '../../print/printWindow';
import {
  DEFAULT_PRINT_PAGE,
  PRINT_ROW_CAP,
  initialPrintOptions,
  type ListPrintConfig,
  type PrintOptionValue,
  type PrintOptionValues,
  type PrintScope,
} from './contracts';

export type PrintPhase = 'idle' | 'configuring' | 'loading' | 'preview';

export interface UsePrintJobParams<TRow> {
  config: ListPrintConfig<TRow> | undefined;
  /** Pulls every row under the current query — the same provider export uses. */
  fetchAllRows?: () => Promise<TRow[]>;
  selectedRows: readonly TRow[];
  /** The built-in sheet, supplied by whoever owns the columns. */
  renderDefault: (rows: readonly TRow[]) => ReactNode;
  /** `document.title` of the print window. */
  documentTitle: string;
}

export interface PrintJob {
  phase: PrintPhase;
  error: string | null;
  options: PrintOptionValues;
  setOption: (id: string, value: PrintOptionValue) => void;
  start: (scope: PrintScope) => void;
  confirm: () => Promise<void>;
  cancel: () => void;
  /** The print window's mount node, or null in every phase but `preview`. */
  mount: HTMLElement | null;
  /** The resolved sheet, or null in every phase but `preview`. */
  sheet: ReactNode | null;
}

export function usePrintJob<TRow>({
  config,
  fetchAllRows,
  selectedRows,
  renderDefault,
  documentTitle,
}: UsePrintJobParams<TRow>): PrintJob {
  const { t, i18n } = useResourceKit();
  const [phase, setPhase] = useState<PrintPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [options, setOptions] = useState<PrintOptionValues>(() =>
    initialPrintOptions(config?.options),
  );
  const [sheet, setSheet] = useState<ReactNode | null>(null);
  const [mount, setMount] = useState<HTMLElement | null>(null);
  const scopeRef = useRef<PrintScope>('all');
  const documentRef = useRef<PrintDocument | null>(null);
  /**
   * Guards against a resolved run landing after the operator cancelled or
   * started another. Without it a slow first run can overwrite the second's
   * sheet — the classic stale-async write.
   */
  const runIdRef = useRef(0);

  const resolveRows = useCallback(
    async (scope: PrintScope): Promise<readonly TRow[]> => {
      if (scope === 'selected') return selectedRows;
      if (!fetchAllRows) return [];
      return fetchAllRows();
    },
    [fetchAllRows, selectedRows],
  );

  /** Back to where the operator can act: the dialog if there is one. */
  const failTo = useCallback(
    (message: string) => {
      setSheet(null);
      setMount(null);
      setError(message);
      // Never `preview` — an error state must not leave a sheet mountable.
      setPhase(config?.options?.length ? 'configuring' : 'idle');
    },
    [config],
  );

  const run = useCallback(
    async (scope: PrintScope, chosen: PrintOptionValues): Promise<void> => {
      const runId = ++runIdRef.current;

      // Opened FIRST, with nothing awaited before it: a `window.open`
      // outside the click's synchronous call stack is a blocked popup.
      const printDoc = openPrintDocument({
        title: documentTitle,
        lang: i18n.language,
        page: {
          size: config?.page?.size ?? DEFAULT_PRINT_PAGE.size,
          margin: config?.page?.margin ?? DEFAULT_PRINT_PAGE.margin,
        },
        hint: t('rk.print.window.hint'),
        status: t('rk.print.window.preparing'),
      });
      if (printDoc === null) {
        failTo(t('rk.print.window.blocked'));
        return;
      }
      documentRef.current?.close();
      documentRef.current = printDoc;

      setPhase('loading');
      setError(null);
      try {
        const rows = await resolveRows(scope);
        const cap = config?.rowCap ?? PRINT_ROW_CAP;
        if (rows.length > cap) {
          // Checked BEFORE the build: the point of a cap is to avoid
          // the expensive step, not to discard its result.
          throw new Error(t('rk.print.tooManyRows', { limit: cap }));
        }
        const built = config?.buildSheet
          ? await config.buildSheet({ rows, options: chosen, scope })
          : renderDefault(rows);
        // Re-checked AFTER the await: `buildSheet` may be async, and a
        // run the operator has already abandoned must not mount.
        if (runId !== runIdRef.current) {
          printDoc.close();
          return;
        }

        printDoc.setStatus(null);
        setSheet(built);
        setMount(printDoc.mount);
        setPhase('preview');
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        if (runId !== runIdRef.current) {
          printDoc.close();
          return;
        }
        // The window is already on screen. A blank one is the single
        // outcome that tells the operator nothing.
        printDoc.setStatus(message);
        failTo(message);
      }
    },
    [config, documentTitle, failTo, i18n.language, renderDefault, resolveRows, t],
  );

  const start = useCallback(
    (scope: PrintScope) => {
      scopeRef.current = scope;
      setError(null);
      const seeded = initialPrintOptions(config?.options);
      setOptions(seeded);
      if (config?.options?.length) {
        setPhase('configuring');
        return;
      }
      void run(scope, seeded);
    },
    [config, run],
  );

  const confirm = useCallback(() => run(scopeRef.current, options), [options, run]);

  const cancel = useCallback(() => {
    runIdRef.current += 1;
    documentRef.current?.close();
    documentRef.current = null;
    setPhase('idle');
    setSheet(null);
    setMount(null);
    setError(null);
  }, []);

  const setOption = useCallback((id: string, value: PrintOptionValue) => {
    setOptions((prev) => ({ ...prev, [id]: value }));
  }, []);

  /**
   * The operator closing the print window ends the run. Without this the app
   * keeps a portal pointed at a document nobody can see, and the next run
   * has to tear down a window that is already gone.
   */
  useEffect(() => {
    const printDoc = documentRef.current;
    if (phase !== 'preview' || printDoc === null) return;
    const gone = () => cancel();
    printDoc.window.addEventListener('pagehide', gone);
    return () => printDoc.window.removeEventListener('pagehide', gone);
  }, [phase, cancel]);

  return { phase, error, options, setOption, start, confirm, cancel, mount, sheet };
}
